import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MfgappsApiService } from '../../../../../services/mfgapps-api.service';
import { ReportingDataService } from '../../../shared/services/reporting-data.service';

type MapPinStatus = 'enabled' | 'progress' | 'planned';
type PinLabelDirection = 'up' | 'down' | 'left' | 'right';

interface NamcShopStatus {
  name: string;
  status: MapPinStatus;
  /** Phase numbers (0-based) for which this shop is enabled. */
  enabledPhases?: number[];
  /** Phase numbers (0-based) for which this shop is in progress. */
  inProgressPhases?: number[];
  /** Phase numbers (0-based) for which this shop is planned. */
  plannedPhases?: number[];
}

interface PinPosition {
  left: string;
  top: string;
  labelDirection: PinLabelDirection;
  /** Perpendicular shift (px) applied to the label to de-clutter crowded pins. */
  labelNudge?: string;
  /** Leader line length (px). Stagger this to separate closely spaced labels. */
  leaderLen?: string;
}

interface DeploymentMapPin extends PinPosition {
  code: string;
  companyId: number;
  companyName: string;
  status: MapPinStatus;
}

interface DeploymentSummaryCard {
  label: string;
  value: string | number;
}

interface DeploymentPortfolioCompany {
  company_id: number;
  company_name: string;
  corporate_id: number;
  id: number;
  company_code: string;
  status: string;
}

interface DeploymentStatusData {
  current_phase: number;
  next_milestone: string;
  status: {
    planned: number;
    enabled: number;
    in_progress: number;
    remaining: number;
    completion_rate: number;
  };
  shops: NamcShopStatus[];
}

interface DeploymentMilestoneRow {
  plant: string;
  companyId: number;
  totalShops: number;
  completed: number;
  inProgress: number;
  completionPct: number;
  nextMilestone: string;
  shops: NamcShopStatus[];
  loaded: boolean;
  loading: boolean;
}

interface DeploymentPhase {
  number: number;
  title: string;
  color: string;
}

@Component({
  selector: 'app-deployment-summary',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './deployment-summary.component.html',
  styleUrl: './deployment-summary.component.scss'
})
export class DeploymentSummaryComponent implements OnInit {

  readonly deploymentPhases: DeploymentPhase[] = [
    { number: 0, title: 'PREREQUISITES', color: '#17617f' },
    { number: 1, title: 'GAP ANALYSIS', color: '#a9a9ab' },
    { number: 2, title: 'NETWORK INSTALL', color: '#ef762b' },
    { number: 3, title: 'SERVER SET INSTALL', color: '#fdbb22' },
    { number: 4, title: 'HW/SW INSTALL', color: '#5791c9' },
    { number: 5, title: 'MODEL LINE DEPLOYMENT', color: '#4da52d' },
    { number: 6, title: 'MDT - NAMC TRANSFER', color: '#7638a1' },
    { number: 7, title: 'ADOPTION & NAMC SCALING', color: '#0873b9' }
  ];
  readonly deploymentMapPins: DeploymentMapPin[] = [];

  /** Static map coordinates keyed by company code (positions are not returned by the API). */
  private readonly pinPositions: Record<string, PinPosition> = {
    TABC:  { left: '14.3%', top: '48.8%', labelDirection: 'up' },
    TMMBC: { left: '15.7%', top: '52.6%', labelDirection: 'down' },
    MTMUS: { left: '62.5%', top: '56.4%', labelDirection: 'right' },
    TMMI:  { left: '57.5%', top: '47.5%', labelDirection: 'up' },
    TMMK:  { left: '61.5%', top: '48.7%', labelDirection: 'up', leaderLen: 'clamp(32px, 8cqw, 60px)' },
    TMMTN: { left: '41.3%', top: '55.0%', labelDirection: 'left' },
    // Separate WV's label from NC's enlarged selected/hovered dot on compact maps.
    TMMWV: { left: '66.5%', top: '47.4%', labelDirection: 'up' },
    TMMMS: { left: '59.8%', top: '55.5%', labelDirection: 'left' },
    TMMAL: { left: '61.0%', top: '58.5%', labelDirection: 'down' },
    // Put TX's label below its dot to keep clear of TN when the map narrows.
    TMMTX: { left: '41.3%', top: '64.8%', labelDirection: 'down' },
    TMMGT: { left: '37.4%', top: '83.2%', labelDirection: 'up' },
    TMMC:  { left: '61.0%', top: '22.7%', labelDirection: 'up' },
    TBMNC: { left: '69.5%', top: '50.5%', labelDirection: 'right', leaderLen: 'clamp(36px, 9cqw, 64px)' },
    TMMMO: { left: '53.5%', top: '46.5%', labelDirection: 'left' }
  };

  selectedMilestoneNamc = 'ALL';
  selectedMilestoneShopType = 'ALL';

  // ── Map zoom ────────────────────────────────────────────────────
  mapZoom = 1;
  readonly minZoom = 1;
  readonly maxZoom = 2.5;
  private readonly zoomStep = 0.25;

  // ── Deployment summary (analytics API) ────────────────────────────────────
  summaryCards: DeploymentSummaryCard[] = [];
  selectedPin: DeploymentMapPin | null = null;
  selectedStatus: DeploymentStatusData | null = null;
  selectedShop: NamcShopStatus | null = null;
  summaryLoading = true;
  statusLoading = false;

  /** Live per-NAMC milestone rows sourced from the deployment status API. */
  milestoneRows: DeploymentMilestoneRow[] = [];

  // ── Milestone table expand / filter ──────────────────────────────────────
  milestoneExpandedNamcs: Record<string, boolean> = {};

  constructor(
    private readonly apiService: MfgappsApiService,
    private readonly dataService: ReportingDataService
  ) {}

  ngOnInit(): void {
    void this.loadDeploymentSummary();
  }

  get filteredMilestoneRows(): DeploymentMilestoneRow[] {
    const namc = this.selectedMilestoneNamc;
    return namc === 'ALL'
      ? this.milestoneRows
      : this.milestoneRows.filter(r => r.plant === namc);
  }

  get selectedPhaseLabel(): string {
    const phase = this.deploymentPhases.find(p => p.number === (this.selectedStatus?.current_phase ?? -1));
    return phase ? phase.title : '';
  }

  get selectedMapCompletionPct(): number {
    return this.selectedStatus?.status.completion_rate ?? 0;
  }

  get selectedMapRemaining(): number {
    return this.selectedStatus?.status.remaining ?? 0;
  }

  selectMapNamc(pin: DeploymentMapPin): void {
    if (this.selectedPin?.code === pin.code) {
      return;
    }
    this.selectedPin = pin;
    this.selectedShop = null;
    void this.loadDeploymentStatus(pin);
  }

  selectShopTile(shop: NamcShopStatus): void {
    this.selectedShop = this.selectedShop?.name === shop.name ? null : shop;
  }

  isShopTileSelected(shop: NamcShopStatus): boolean {
    return this.selectedShop?.name === shop.name;
  }

  isMapNamcSelected(code: string): boolean {
    return this.selectedPin?.code === code;
  }

  isPhaseDone(phaseNumber: number): boolean {
    if (!this.selectedShop) {
      return false;
    }
    return this.selectedShop.enabledPhases?.includes(phaseNumber) ?? false;
  }

  isPhaseCurrent(_phaseNumber: number): boolean {
    return false;
  }

  getMapPinAriaLabel(pin: DeploymentMapPin): string {
    return `${pin.code}: ${pin.companyName}`;
  }

  zoomIn(): void {
    this.mapZoom = Math.min(this.maxZoom, Math.round((this.mapZoom + this.zoomStep) * 100) / 100);
  }

  zoomOut(): void {
    this.mapZoom = Math.max(this.minZoom, Math.round((this.mapZoom - this.zoomStep) * 100) / 100);
  }

  toggleMilestoneNamc(namc: string): void {
    const willExpand = !this.milestoneExpandedNamcs[namc];
    this.milestoneExpandedNamcs[namc] = willExpand;
    if (willExpand) {
      const row = this.milestoneRows.find(r => r.plant === namc);
      if (row && !row.loaded && !row.loading) {
        void this.loadMilestoneRowStatus(row);
      }
    }
  }

  isMilestoneNamcExpanded(namc: string): boolean {
    return !!this.milestoneExpandedNamcs[namc] || this.selectedMilestoneShopType !== 'ALL';
  }

  onMilestoneNamcFilterChange(selected: string): void {
    const fresh: Record<string, boolean> = {};
    this.milestoneRows.forEach(r => {
      fresh[r.plant] = selected !== 'ALL' && r.plant === selected;
    });
    this.milestoneExpandedNamcs = fresh;
  }

  getMilestoneShopRows(namcCode: string): Array<{ name: string; status: MapPinStatus }> {
    const row = this.milestoneRows.find(r => r.plant === namcCode);
    if (!row) { return []; }

    const shopType = this.selectedMilestoneShopType;
    return shopType === 'ALL'
      ? row.shops
      : row.shops.filter(s => this.dataService.shopMatchesType(s.name, shopType));
  }

  shopStatusCompletion(status: MapPinStatus): number {
    return status === 'enabled' ? 100 : status === 'progress' ? 50 : 0;
  }

  // ── trackBy helpers ──────────────────────────────────────────────────────
  trackByIndex(index: number): number                        { return index; }
  trackByPlant(_: number, row: { plant: string }): string    { return row.plant; }
  trackByMapCode(_: number, pin: DeploymentMapPin): string    { return pin.code; }
  trackByPhaseNumber(_: number, phase: DeploymentPhase): number { return phase.number; }

  // ── Private ──────────────────────────────────────────────────────────────

  private async loadDeploymentSummary(): Promise<void> {
    this.summaryLoading = true;
    try {
      const response = await this.apiService.getDeploymentSummary();
      const raw = response?.result ?? response;

      this.summaryCards = Array.isArray(raw?.cards)
        ? raw.cards.map((card: any) => ({ label: card?.label ?? '', value: card?.value ?? '' }))
        : [];

      const portfolio: DeploymentPortfolioCompany[] = Array.isArray(raw?.deployment_portfolio)
        ? raw.deployment_portfolio
        : [];
      this.deploymentMapPins.length = 0;
      this.deploymentMapPins.push(...this.buildMapPins(portfolio));
      const defaultCode = getDeploymentDefaultCode();
      
      const defaultPin = await selectDeploymentMapPin(this.deploymentMapPins, defaultCode);
      
      if (defaultPin) {
        this.selectedPin = defaultPin;
        await this.loadDeploymentStatus(defaultPin);
      }

      // Build milestone rows from the portfolio; shop details are filled in below via a single bulk call.
      this.milestoneRows = portfolio.map(company => ({
        plant: company.company_code,
        companyId: company.id,
        totalShops: 0,
        completed: 0,
        inProgress: 0,
        completionPct: 0,
        nextMilestone: '',
        shops: [],
        loaded: false,
        loading: false
      }));
      await this.loadAllMilestoneStatuses();
    }
    catch (error) {
      console.info('Unable to load deployment summary', error);
      this.summaryCards = [];
      this.deploymentMapPins.length = 0;
    }
    finally {
      this.summaryLoading = false;
    }
  }

  /**
   * Fetches deployment status (including shop enablement) for every NAMC in a single
   * API call and fills in all milestone rows up front, so expanding a row's arrow
   * never triggers a per-NAMC network request.
   */
  private async loadAllMilestoneStatuses(): Promise<void> {
    try {
      const response = await this.apiService.getAllDeploymentStatus();
      const raw = (response?.result ?? response) ?? {};
      this.milestoneRows.forEach(row => {
        const companyRaw = raw[row.plant] ?? raw[row.plant?.toUpperCase?.() ?? row.plant];
        if (!companyRaw) {
          return;
        }
        const status = this.normalizeDeploymentStatus(companyRaw);
        row.totalShops = status.status.planned;
        row.completed = status.status.enabled;
        row.inProgress = status.status.in_progress;
        row.completionPct = status.status.completion_rate;
        row.nextMilestone = status.next_milestone;
        row.shops = status.shops;
        row.loaded = true;
      });
    }
    catch (error) {
      console.info('Unable to load deployment milestone statuses', error);
    }
  }

  /** Fallback: fetches a single NAMC's deployment status if the bulk load above missed it. */
  private async loadMilestoneRowStatus(row: DeploymentMilestoneRow): Promise<void> {
    row.loading = true;
    try {
      const response = await this.apiService.getDeploymentStatus(row.companyId);
      const status = this.normalizeDeploymentStatus(response?.result ?? response);
      row.totalShops = status.status.planned;
      row.completed = status.status.enabled;
      row.inProgress = status.status.in_progress;
      row.completionPct = status.status.completion_rate;
      row.nextMilestone = status.next_milestone;
      row.shops = status.shops;
      row.loaded = true;
    }
    catch (error) {
      console.info('Unable to load deployment milestone status', error);
    }
    finally {
      row.loading = false;
    }
  }

  private buildMapPins(portfolio: DeploymentPortfolioCompany[]): DeploymentMapPin[] {
    let fallbackIndex = 0;
    return portfolio.map(company => {
      const position = this.pinPositions[company.company_code] ?? this.getFallbackPosition(fallbackIndex++);
      console.log(position);
      return {
        ...position,
        code: company.company_code,
        companyId: company.id,
        companyName: company.company_name,
        status: this.toPinStatus(company.status)
      } as DeploymentMapPin;
    });
  }

  /**
   * Generates staggered default coordinates for companies whose code is not in
   * `pinPositions`. New NAMCs land in an open area on the right side of the map
   * and are spread across a small grid so they don't overlap.
   */
  private getFallbackPosition(index: number): PinPosition {
    const columns = 3;
    const startLeft = 80; // % — open water area on the right of the map
    const startTop = 66;  // %
    const stepX = 6;      // % between columns
    const stepY = 8;      // % between rows
    return {
      left: `${startLeft + (index % columns) * stepX}%`,
      top: `${startTop + Math.floor(index / columns) * stepY}%`,
      labelDirection: 'left'
    };
  }

  private async loadDeploymentStatus(pin: DeploymentMapPin): Promise<void> {
    this.statusLoading = true;
    this.selectedStatus = null;
    this.selectedShop = null;
    try {
      const response = await this.apiService.getDeploymentStatus(pin.companyId);
      const raw = response?.result ?? response;
      this.selectedStatus = this.normalizeDeploymentStatus(raw);
      this.selectedShop = null;
    }
    catch (error) {
      console.info('Unable to load deployment status', error);
      this.selectedStatus = null;
      this.selectedShop = null;
    }
    finally {
      this.statusLoading = false;
    }
  }

  private normalizeDeploymentStatus(raw: any): DeploymentStatusData {
    const shops: NamcShopStatus[] = Array.isArray(raw?.shops)
      ? raw.shops.map((shop: any) => ({
          name: shop?.shop_name ?? '',
          status: this.toPinStatus(shop?.status),
          enabledPhases: this.extractPhaseArray(shop?.phases?.enabled),
          inProgressPhases: this.extractPhaseArray(shop?.phases?.in_progress),
          plannedPhases: this.extractPhaseArray(shop?.phases?.planned)
        }))
      : [];


    return {
      current_phase: Number(raw?.current_phase) || 0,
      next_milestone: raw?.next_milestone ?? '',
      status: this.computeStatusCounts(shops),
      shops
    };
  }

  /**
   * Derive Total Shops / Enabled / In Progress / Planned(Not Started) / Completion%
   * directly from the shops array rather than trusting the API's status block.
   * We repurpose the DeploymentStatusData.status fields:
   *   - planned         = total shop count
   *   - enabled         = shops with status ENABLED
   *   - in_progress     = shops with status IN PROGRESS
   *   - remaining       = shops with status PLANNED (Not Started)
   *   - completion_rate = enabled / total * 100
   */
  private computeStatusCounts(shops: NamcShopStatus[]): DeploymentStatusData['status'] {
    const total = shops.length;
    const enabled = shops.filter(s => s.status === 'enabled').length;
    const inProgress = shops.filter(s => s.status === 'progress').length;
    const notStarted = shops.filter(s => s.status === 'planned').length;
    const completionRate = total === 0 ? 0 : Math.round((enabled / total) * 1000) / 10;
    return {
      planned: total,
      enabled,
      in_progress: inProgress,
      remaining: notStarted,
      completion_rate: completionRate
    };
  }

  private extractPhaseArray(raw: any): number[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map(Number)
      .filter((n: number) => Number.isInteger(n) && n >= 0 && n <= 7);
  }

 
  private toPinStatus(status: string | undefined): MapPinStatus {
    const normalized = (status ?? '').toLowerCase();
    if (normalized.includes('progress')) {
      return 'progress';
    }
    if (normalized.includes('enabled') || normalized.includes('complete')) {
      return 'enabled';
    }
    return 'planned';
  }
}

function selectDeploymentMapPin(deploymentMapPins: DeploymentMapPin[], defaultCode: string): DeploymentMapPin | null {
  
  const defaultpin = deploymentMapPins.find(pin => pin.code.toUpperCase() == defaultCode.toUpperCase()) ?? null;
 
  return defaultpin ?? deploymentMapPins[0] ?? null;
}

function getDeploymentDefaultCode(): string { return "TMMI"; }
